import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Output, afterRenderEffect, computed, effect, inject, input, linkedSignal, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommentAuthorType, SupportMessageView, SupportThreadView } from 'src/app/common/models/support/admin-support.models';
import { EmailMessageComponent } from 'src/app/shared/email-message/email-message.component';
import { OsloDatePipe } from 'src/app/pipes/oslo-date.pipe';

const DRAFT_KEY_PREFIX = 'admin-support-draft:';

@Component({
  selector: 'app-case-thread',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, EmailMessageComponent, OsloDatePipe],
  templateUrl: './case-thread.component.html',
  styleUrls: ['./case-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaseThreadComponent {
  thread = input<SupportThreadView | null>(null);
  sending = input<boolean>(false);
  closing = input<boolean>(false);

  @Output() back = new EventEmitter<void>();
  @Output() reply = new EventEmitter<string>();
  @Output() closeCase = new EventEmitter<void>();

  // Keyed to the open case so switching threads (or reloading the page) restores the right in-progress reply.
  replyText = linkedSignal(() => this.loadDraft(this.thread()?.supportId));

  replyCharCount = computed(() => this.replyText().length);

  composerOpen = signal(false);

  latestCustomerMessage = computed(() => {
    const messages = this.thread()?.messages ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].authorType === 'Member') return messages[i];
    }
    return null;
  });

  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly replyTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('replyTextarea');

  constructor() {
    effect(() => {
      const supportId = this.thread()?.supportId;
      const text = this.replyText();
      if (!supportId) return;
      if (text) localStorage.setItem(this.draftKey(supportId), text);
      else localStorage.removeItem(this.draftKey(supportId));
    });

    // Grows the inline reply box with its content, capped at half the panel height, so a
    // long reply stays reviewable without the whole thread panel being taken over. While
    // there's text, this only grows, never shrinks -- scrollHeight reflects the full content
    // height regardless of the current explicit height, so there's no need to reset the
    // height to measure it, which would otherwise overwrite a height the admin set by
    // dragging the resize handle. Once the box is emptied (reply sent, or a freshly opened
    // case with no draft), the explicit height is dropped so the box returns to its default
    // size for the next reply instead of staying stuck at its last grown height.
    afterRenderEffect({
      earlyRead: () => {
        const text = this.replyText();
        const textarea = this.replyTextarea()?.nativeElement;
        if (!textarea) return null;
        if (!text) return { textarea, height: '' };
        const maxHeight = this.hostRef.nativeElement.clientHeight * 0.5;
        const desiredHeight = Math.min(textarea.scrollHeight, maxHeight);
        return desiredHeight > textarea.clientHeight ? { textarea, height: `${desiredHeight}px` } : null;
      },
      write: (result) => {
        const change = result();
        if (change) change.textarea.style.height = change.height;
      },
    });
  }

  private draftKey(supportId: string): string {
    return `${DRAFT_KEY_PREFIX}${supportId}`;
  }

  private loadDraft(supportId: string | undefined): string {
    if (!supportId) return '';
    return localStorage.getItem(this.draftKey(supportId)) ?? '';
  }

  onBack() {
    this.back.emit();
  }

  onCloseCase() {
    if (this.closing()) return;
    this.closeCase.emit();
  }

  onSendReply() {
    if (this.sending()) return;
    const text = this.replyText().trim();
    if (!text) return;
    this.reply.emit(text);
    this.replyText.set('');
    this.composerOpen.set(false);
  }

  onReplyKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || (!event.ctrlKey && !event.metaKey)) return;
    event.preventDefault();
    this.onSendReply();
  }

  onExpandComposer() {
    this.composerOpen.set(true);
  }

  onCloseComposer() {
    this.composerOpen.set(false);
  }

  // Record<CommentAuthorType, string> forces every union member to be mapped here --
  // adding a new CommentAuthorType without updating this map is a compile error,
  // matching the same pattern applied to SupportTabComponent.authorClass.
  private static readonly AUTHOR_CLASSES: Record<CommentAuthorType, string> = {
    Member: 'member',
    AI: 'ai',
    System: 'system',
    Admin: 'admin',
  };

  authorClass(authorType: CommentAuthorType): string {
    return CaseThreadComponent.AUTHOR_CLASSES[authorType] ?? 'admin';
  }

  // Replaces the placeholder "Unknown Sender" label with the real sender's email.
  authorLabel(message: SupportMessageView): string {
    const unmatchedSenderEmail = this.thread()?.unmatchedSenderEmail;
    return message.authorType === 'Member' && unmatchedSenderEmail ? unmatchedSenderEmail : message.authorLabel;
  }
}
